/* eslint-disable @harmony/stencil/no-tailwind-disable */
import React, { createContext,
  useEffect,
  useState, } from 'react';
import {
  bool, func, oneOf, node,
  arrayOf,
  element,
  oneOfType,
} from 'prop-types';
import classnames from 'classnames';
import { DraggableDrawer } from './DraggableDrawer';
// eslint-disable-next-line @harmony/stencil/no-import-css
import './AssistantDrawer.scss';

export const DrawerContext = createContext({});

/**
 * Wrapper for the Assistant's drawer that handles which version is displayed.
 * Criteria based on the channel being used (mobile or tablet/desktop).
 *
 * @param { AssistantDrawerProps } props - The properties for the AssistantDrawer component.
 * - `children`: Content to display inside the drawer.
 * - `drawerState`: The state of the mobile drawer.
 * - `setDrawerState`: Function to update drawerState.
 * - `onClose`: Close event callback.
 * - `isMobile`: Check if `channel` is mobile.
 *
 * @returns {React.JSX.Element|false} The AssistantDrawer component.
 */
const AssistantDrawer = ({
  children,
  drawerState,
  setDrawerState,
  onClose,
  isMobile,
}) => {

  const [isMounted, setIsMounted] = useState(false);
  const closeDrawer = (event, fromCloseButton) => {
    if (event) {
      event.preventDefault();
    }
    setDrawerState('CLOSED');
    if (onClose) {
      onClose(fromCloseButton);
    }
  };
  const getDrawerPositionStyles = ({
    toggleFlag,
  }) => {
    return {
      transition: 'right .3s ease-in',
      opacity: toggleFlag ? 1 : 0,
      // sticky header height (145px) + margin (12px)
      top: '157px',
      border: '1px solid #CCC',
      position: 'sticky',
      // full height of viewport - (sticky header height + margin)
      maxHeight: 'calc(100vh - 157px)',
      right: 0,
      // full height of viewport - (sticky header height + double margin)
      height: 'calc(100vh - 169px)',
    };
  };
  // eslint-disable-next-line tailwindcss/no-arbitrary-value, tailwindcss/no-custom-classname
  const drawerClasses = classnames('drawer-container sui-w-[300px] xl:sui-w-[400px]', {
    'sui-hidden': drawerState !== 'OPEN',
    'sui-flex sui-flex-col': drawerState === 'OPEN',
  });
  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('drawer.close', () => {
      closeDrawer(null, true);
    });
  }, []);
  useEffect(() => {
    if (!isMobile) {
      const rootElement = document.getElementById('root');
      if (drawerState === 'OPEN' && rootElement) {
        rootElement.classList.add('assistant-appended-root-styles');

        const pipContainer = document.getElementById('#pip_sponsored');
        const callback = () => {
          const swiperContainer = pipContainer && pipContainer.querySelector('div.swiper');
          if (drawerState === 'OPEN' && swiperContainer) {
            swiperContainer.classList.add('assistant-drawer-open');
          } else if (swiperContainer) {
            swiperContainer.classList.remove('assistant-drawer-open');
          }
        };
        const observer = new MutationObserver(callback);
        if (pipContainer) {
          observer.observe(pipContainer, {
            attributes: false,
            childList: true,
            subtree: false
          });
        }
      } else if (rootElement) {
        rootElement.classList.remove('assistant-appended-root-styles');
      }
    }

    return () => {
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.classList.remove('assistant-appended-root-styles');
        rootElement.classList.remove('sui-grid');
        rootElement.style.gridTemplateColumns = '';
        const gridSibling = rootElement.querySelector('div.grid.isBound');
        if (gridSibling) {
          gridSibling.classList.remove('assistant-drawer-sibling');
        }
        const pipContainer = document.getElementById('#pip_sponsored');
        const swiperContainer = pipContainer && pipContainer.querySelector('div.swiper');
        if (swiperContainer) {
          swiperContainer.classList.remove('assistant-drawer-open');
        }
      }
    };
  }, [drawerState, isMobile]);

  return isMobile ? (
    <DraggableDrawer
      drawerState={drawerState}
      setDrawerState={setDrawerState}
      onClose={onClose}
    >
      {children}
    </DraggableDrawer>
  ) : isMounted && (
    <DrawerContext.Provider
      value={{
        closeDrawer,
        isDrawerOpen: drawerState === 'OPEN',
      }}
    >
      <aside
        className={drawerClasses}
        style={getDrawerPositionStyles({
          toggleFlag: drawerState === 'OPEN',
        })}
        data-testid="drawer"
      >
        {children}
      </aside>
    </DrawerContext.Provider>
  );
};

AssistantDrawer.displayName = 'AssistantDrawer';

AssistantDrawer.propTypes = {
  children: oneOfType([
    node,
    element,
    arrayOf(element)
  ]).isRequired,
  drawerState: oneOf(['OPEN', 'MINIMIZED', 'CLOSED']).isRequired,
  setDrawerState: func.isRequired,
  onClose: func,
  isMobile: bool.isRequired,
};

AssistantDrawer.defaultProps = {
  onClose: () => {},
};

export { AssistantDrawer };
